// Plain wording for the phone and tablet home screen. Pure functions, no screen code.

// "Good morning", "Good afternoon" or "Good evening" for an hour of the day (0 to 23, local time).
export function greetingFor(hour) {
  if (typeof hour !== "number" || !isFinite(hour)) return "Hello";
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

// "Good morning, Demo business". Without a name it is just the greeting.
export function welcomeLine(hour, name) {
  const g = greetingFor(hour);
  const n = (name || "").trim();
  return n ? `${g}, ${n}` : g;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// "2026-03-01" -> "Mar 1, 2026", read from the text so the time zone can never shift the day. Anything else gives "".
export function plainDate(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || "");
  if (!m) return "";
  const mo = Number(m[2]);
  if (mo < 1 || mo > 12) return "";
  return `${MONTHS[mo - 1]} ${Number(m[3])}, ${m[1]}`;
}

// One sentence naming the period the home numbers cover.
export function periodLine(rangeKey, customStart, customEnd) {
  switch (rangeKey) {
    case "mtd": return "Here's how your jobs are doing this month.";
    case "qtd": return "Here's how your jobs are doing this quarter.";
    case "ytd": return "Here's how your jobs are doing this year.";
    case "prior_year": return "Here's how your jobs did last year.";
    case "all": return "Here's how your jobs have done across all time.";
    case "custom": {
      const a = plainDate(customStart), b = plainDate(customEnd);
      if (a && b) return `Here's how your jobs did from ${a} to ${b}.`;
      if (a) return `Here's how your jobs did since ${a}.`;
      if (b) return `Here's how your jobs did up to ${b}.`;
      return "Pick the dates you want to look at.";
    }
    default: return "Here's how your jobs are doing.";
  }
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// The "Needs your attention" rows, most important first. Each row: { key, text }. Rows with a zero count are left out.
// Order: expenses to review, losing money, trending toward a loss, no invoice yet, missing numbers, low margin.
// counts: { expenses, loss, atrisk, unbilled, incomplete, lowmargin }. showExpenses is false for businesses with no Expense Management screen.
export function attentionRows(counts, showExpenses = true) {
  const c = counts || {};
  const rows = [];
  if (showExpenses && c.expenses > 0) rows.push({ key: "expenses", text: `${plural(c.expenses, "expense", "expenses")} to review` });
  if (c.loss > 0) rows.push({ key: "loss", text: c.loss === 1 ? "1 job is losing money" : `${c.loss} jobs are losing money` });
  if (c.atrisk > 0) rows.push({ key: "atrisk", text: c.atrisk === 1 ? "1 job is trending toward a loss" : `${c.atrisk} jobs are trending toward a loss` });
  if (c.unbilled > 0) rows.push({ key: "unbilled", text: c.unbilled === 1 ? "1 job has costs but no invoice yet" : `${c.unbilled} jobs have costs but no invoice yet` });
  if (c.incomplete > 0) rows.push({ key: "incomplete", text: c.incomplete === 1 ? "1 job is missing numbers" : `${c.incomplete} jobs are missing numbers` });
  if (c.lowmargin > 0) rows.push({ key: "lowmargin", text: c.lowmargin === 1 ? "1 job has a low margin" : `${c.lowmargin} jobs have a low margin` });
  return rows;
}

// Plain names for each job filter, used in "Showing ..." and in the empty job list.
export const FILTER_NAMES = {
  unbilled: "jobs with no invoice yet",
  loss: "jobs losing money",
  atrisk: "jobs trending toward a loss",
  incomplete: "jobs missing numbers",
  lowmargin: "jobs with a low margin",
};

// Empty job list with a Home filter on, e.g. "No jobs losing money in this period."
export function emptyFilterLine(filterKey) {
  const n = FILTER_NAMES[filterKey];
  return n ? `No ${n} in this period.` : "No jobs in this period";
}
